import type { Table } from '@tanstack/react-table';
import {
  Check,
  ChevronDown,
  Columns3,
  type LucideIcon,
  MoreHorizontal,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { DateRange } from 'react-day-picker';

import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import { InputDatePicker } from '~shared/components/input-date-picker';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button, type ButtonProps } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '~shared/shadcn/ui/command';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';

import type { EnrollioTableOption } from './enrollio-table.types';

export function EnrollioTableToolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function EnrollioTableFilters({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-col gap-2 rounded-lg border bg-muted/30 p-3 md:flex-row md:flex-wrap md:items-end',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function EnrollioTableTotal({
  label,
  total,
}: {
  label: ReactNode;
  total: number;
}) {
  return (
    <div className='flex items-center gap-2'>
      <span className='font-semibold'>{label}</span>
      <Badge variant='secondary'>{total.toLocaleString()}</Badge>
    </div>
  );
}

export function EnrollioTableSearch<TData>({
  table,
  value,
  onChange,
  placeholder = 'Search...',
  className,
}: {
  table?: Table<TData>;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const globalFilter = table?.getState().globalFilter as unknown;
  const searchValue =
    value ?? (typeof globalFilter === 'string' ? globalFilter : '');
  const handleChange = (nextValue: string) => {
    if (onChange) {
      onChange(nextValue);
    } else {
      table?.setGlobalFilter(nextValue);
    }
  };

  return (
    <div className={cn('relative w-full md:w-72', className)}>
      <Search className='absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
      <Input
        type='search'
        value={searchValue}
        placeholder={placeholder}
        className='pl-8'
        onChange={(event) => {
          handleChange(event.target.value);
        }}
      />
    </div>
  );
}

const headerButtonClasses = {
  primary: '',
  secondary: '',
  outline: '',
} as const;

export function EnrollioTableHeaderButton({
  kind = 'primary',
  icon: Icon,
  children,
  className,
  ...props
}: ButtonProps & {
  kind?: keyof typeof headerButtonClasses;
  icon?: LucideIcon;
}) {
  const variant: ButtonProps['variant'] =
    kind === 'primary'
      ? 'default'
      : kind === 'secondary'
        ? 'secondary'
        : 'outline';
  return (
    <Button
      variant={variant}
      className={cn(headerButtonClasses[kind], className)}
      {...props}
    >
      {Icon ? <Icon className='size-4' /> : null}
      {children}
    </Button>
  );
}

export function EnrollioTableSelectFilter({
  value,
  onChange,
  options,
  placeholder = 'Filter',
  className,
}: {
  value?: string;
  onChange: (value: string) => void;
  options: Array<EnrollioTableOption>;
  placeholder?: string;
  className?: string;
}) {
  const selectedLabel =
    options.find((option) => option.value === value)?.label ??
    (value && value !== 'All' ? value : undefined);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn('w-full md:w-44', className)}>
        {/* Render the label ourselves — Radix SelectValue goes blank when the
            current value has no matching SelectItem yet (async option lists). */}
        <span className='truncate'>{selectedLabel ?? placeholder}</span>
      </SelectTrigger>
      <SelectContent>
        {value &&
        value !== 'All' &&
        !options.some((option) => option.value === value) ? (
          <SelectItem value={value}>{value}</SelectItem>
        ) : null}
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function EnrollioTableSearchFilter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: Array<EnrollioTableOption>;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' className='justify-between'>
          {options.find((option) => option.value === value)?.label ?? label}
          <ChevronDown className='ml-2 size-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='start'>
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No values found.</CommandEmpty>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={`${option.label} ${option.value}`}
                disabled={option.disabled}
                onSelect={() => {
                  onChange(option.value);
                }}
              >
                <Check
                  className={cn(
                    'size-4',
                    value === option.value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function EnrollioTableMultiSelectFilter({
  label,
  values,
  onChange,
  options,
}: {
  label: string;
  values: Array<string>;
  onChange: (values: Array<string>) => void;
  options: Array<EnrollioTableOption>;
}) {
  const toggle = (value: string) => {
    onChange(
      values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value],
    );
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline'>
          <SlidersHorizontal className='mr-2 size-4' />
          {label}
          {values.length ? (
            <Badge variant='secondary' className='ml-2'>
              {values.length}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-2' align='start'>
        <div className='space-y-1'>
          {options.map((option) => (
            <label
              key={option.value}
              className='flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent'
            >
              <Checkbox
                checked={values.includes(option.value)}
                disabled={option.disabled}
                onCheckedChange={() => {
                  toggle(option.value);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
        {values.length ? (
          <Button
            variant='ghost'
            size='sm'
            className='mt-2 w-full'
            onClick={() => {
              onChange([]);
            }}
          >
            <X className='mr-2 size-4' /> Clear
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export function EnrollioTableNumberFilter({
  value,
  onChange,
  label,
  step = 'any',
}: {
  value?: number;
  onChange: (value: number | undefined) => void;
  label: string;
  step?: number | 'any';
}) {
  return (
    <div className='space-y-1'>
      <Label className='sr-only'>{label}</Label>
      <Input
        type='number'
        step={step}
        value={value ?? ''}
        placeholder={label}
        className='w-full md:w-36'
        onChange={(event) => {
          onChange(
            event.target.value === '' ? undefined : Number(event.target.value),
          );
        }}
      />
    </div>
  );
}

export function EnrollioTableToggleFilter({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className='flex items-center gap-2'>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
}

export function EnrollioTableRadioFilter({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<EnrollioTableOption>;
}) {
  return (
    <RadioGroup
      value={value}
      onValueChange={onChange}
      className='flex flex-wrap gap-3'
    >
      {options.map((option) => (
        <div key={option.value} className='flex items-center gap-2'>
          <RadioGroupItem
            id={`table-radio-${option.value}`}
            value={option.value}
            disabled={option.disabled}
          />
          <Label htmlFor={`table-radio-${option.value}`}>{option.label}</Label>
        </div>
      ))}
    </RadioGroup>
  );
}

export function EnrollioTableOverflowFilter({
  label = 'More filters',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' size='icon' aria-label={label}>
          <MoreHorizontal className='size-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-64 p-3'>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function EnrollioTableDateFilter({
  id,
  value,
  onChange,
  label = 'Date',
}: {
  id: string;
  value?: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  return (
    <InputDatePicker
      id={id}
      ariaLabel={label}
      value={value ?? ''}
      onChange={onChange}
      placeholder={label}
      className='w-auto'
    />
  );
}

export function EnrollioTableDateRangeFilter({
  value,
  onChange,
}: {
  value: DateRange | undefined;
  onChange: (value: DateRange | undefined) => void;
}) {
  return <DatePickerWithRange date={value} setDate={onChange} />;
}

export function EnrollioTableColumnToggle<TData>({
  table,
  enableReorder = false,
  onReset,
}: {
  table: Table<TData>;
  enableReorder?: boolean;
  onReset?: () => void;
}) {
  const moveColumn = (columnId: string, direction: -1 | 1) => {
    const current =
      table.getState().columnOrder.length > 0
        ? table.getState().columnOrder
        : table.getAllLeafColumns().map((column) => column.id);
    const index = current.indexOf(columnId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return;
    const next = [...current];
    const [moved] = next.splice(index, 1);
    next.splice(nextIndex, 0, moved);
    table.setColumnOrder(next);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='outline'>
          <Columns3 className='mr-2 size-4' /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-64'>
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllLeafColumns()
          .filter((column) => column.getCanHide())
          .map((column) => (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={column.getIsVisible()}
              className='capitalize'
              onSelect={(event) => {
                event.preventDefault();
              }}
              onCheckedChange={(checked) => {
                column.toggleVisibility(checked);
              }}
            >
              <span className='flex w-full items-center justify-between gap-2'>
                <span>{column.id}</span>
                {enableReorder ? (
                  <span className='flex gap-1'>
                    <button
                      type='button'
                      className='rounded px-1 text-xs'
                      aria-label={`Move ${column.id} earlier`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        moveColumn(column.id, -1);
                      }}
                    >
                      Up
                    </button>
                    <button
                      type='button'
                      className='rounded px-1 text-xs'
                      aria-label={`Move ${column.id} later`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        moveColumn(column.id, 1);
                      }}
                    >
                      Down
                    </button>
                  </span>
                ) : null}
              </span>
            </DropdownMenuCheckboxItem>
          ))}
        {onReset ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                onReset();
              }}
            >
              Reset columns
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
